import { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, DollarSign, Receipt, TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const STATUS_LABEL = {
  paid: { label: 'Pagado', className: 'bg-green-100 text-green-700' },
  initiated: { label: 'Iniciado', className: 'bg-blue-100 text-blue-700' },
  open: { label: 'Abierto', className: 'bg-blue-100 text-blue-700' },
  complete: { label: 'Completo', className: 'bg-green-100 text-green-700' },
  failed: { label: 'Fallido', className: 'bg-red-100 text-red-700' },
  expired: { label: 'Expirado', className: 'bg-gray-100 text-gray-500' },
  refunded: { label: 'Reembolsado', className: 'bg-orange-100 text-orange-700' },
  unknown: { label: 'Desconocido', className: 'bg-gray-100 text-gray-500' },
};

const ROLE_LABEL = { seller: 'Vendedor privado', dealer: 'Concesionario', admin: 'Admin' };

function formatCOP(n) {
  const v = Math.round(Number(n) || 0);
  return `$${v.toLocaleString('es-CO')} COP`;
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

export function BillingTab() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [timeseries, setTimeseries] = useState(null);
  const [chartRange, setChartRange] = useState({ granularity: 'day', days: 30 });
  const limit = 25;

  const load = async (opts = {}) => {
    const isInitial = opts.initial;
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    try {
      const [sumRes, txRes, tsRes] = await Promise.all([
        adminAPI.getBillingSummary(),
        adminAPI.getBillingTransactions({ status: status === 'all' ? undefined : status, search: search || undefined, skip, limit }),
        adminAPI.getBillingTimeseries(chartRange),
      ]);
      setSummary(sumRes.data);
      setTransactions(txRes.data.items || []);
      setTotal(txRes.data.total || 0);
      setTimeseries(tsRes.data);
    } catch (e) {
      toast.error('No se pudo cargar la información de facturación');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load({ initial: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, skip, chartRange.granularity, chartRange.days]); // eslint-disable-line react-hooks/exhaustive-deps

  const applySearch = () => { setSkip(0); load(); };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Revenue trend chart */}
      <RevenueChart data={timeseries} range={chartRange} onRangeChange={setChartRange} />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI icon={<DollarSign className="h-4 w-4" />} label="Ingresos totales" value={formatCOP(summary?.total_revenue)} testid="kpi-total-revenue" />
        <KPI icon={<Calendar className="h-4 w-4" />} label={`Ingresos ${summary?.month_label ? '('+summary.month_label+')' : 'del mes'}`} value={formatCOP(summary?.month_revenue)} testid="kpi-month-revenue" />
        <KPI icon={<Receipt className="h-4 w-4" />} label="Transacciones totales" value={summary?.total_transactions ?? 0} sub={`${summary?.paid_transactions ?? 0} pagadas`} testid="kpi-tx-total" />
        <KPI icon={<TrendingUp className="h-4 w-4" />} label="Ticket promedio" value={formatCOP(summary?.avg_ticket)} testid="kpi-avg-ticket" />
      </div>

      {/* Breakdown by role */}
      {summary?.by_role && Object.keys(summary.by_role).length > 0 && (
        <div className="bg-white border border-border rounded-md p-4">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wide mb-3">Desglose por rol</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(summary.by_role).map(([role, data]) => (
              <div key={role} className="border border-border rounded-sm p-3" data-testid={`role-breakdown-${role}`}>
                <div className="text-xs uppercase text-muted-foreground mb-1">{ROLE_LABEL[role] || role}</div>
                <div className="text-xl font-bold">{formatCOP(data.total)}</div>
                <div className="text-xs text-muted-foreground">{data.count} transacciones</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions table */}
      <div className="bg-white border border-border rounded-md">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Transacciones ({total})</h2>
            <select value={status} onChange={e => { setStatus(e.target.value); setSkip(0); }} className="text-sm border border-border rounded-sm px-2 py-1" data-testid="billing-status-filter">
              <option value="all">Todos los estados</option>
              <option value="paid">Pagado</option>
              <option value="initiated">Iniciado</option>
              <option value="open">Abierto</option>
              <option value="failed">Fallido</option>
              <option value="expired">Expirado</option>
              <option value="refunded">Reembolsado</option>
            </select>
            <div className="flex items-center gap-2">
              <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && applySearch()} placeholder="Buscar por email, nombre o session id" className="w-72" data-testid="billing-search-input" />
              <Button variant="outline" size="sm" onClick={applySearch} data-testid="billing-search-btn">Buscar</Button>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={refreshing} data-testid="billing-refresh-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Actualizar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billing-table">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Fecha</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Cliente</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Rol</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Tipo</th>
                <th className="text-right py-3 px-4 font-semibold uppercase text-xs">Monto</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Anuncios</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Estado</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && (
                <tr><td colSpan={7} className="py-8 px-4 text-center text-muted-foreground">Sin transacciones para mostrar.</td></tr>
              )}
              {transactions.map(tx => {
                const st = STATUS_LABEL[tx.payment_status] || STATUS_LABEL.unknown;
                return (
                  <tr key={tx.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`billing-row-${tx.id}`}>
                    <td className="py-3 px-4 whitespace-nowrap">{formatDate(tx.created_at)}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{tx.user_name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{tx.user_email || '-'}</div>
                    </td>
                    <td className="py-3 px-4">{ROLE_LABEL[tx.user_role] || tx.user_role || '-'}</td>
                    <td className="py-3 px-4">{tx.purpose === 'bulk_publish' ? 'Masivo' : 'Individual'}</td>
                    <td className="py-3 px-4 text-right font-mono whitespace-nowrap">{formatCOP(tx.amount)}</td>
                    <td className="py-3 px-4"><span className="text-xs">{(tx.listing_ids || []).length}</span></td>
                    <td className="py-3 px-4"><Badge className={st.className}>{st.label}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > limit && (
          <div className="flex items-center justify-between p-4 border-t border-border text-sm">
            <span>Página {currentPage} de {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="billing-prev">Anterior</Button>
              <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="billing-next">Siguiente</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-border rounded-md p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase mb-2">{icon}{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

const RANGE_PRESETS = [
  { key: '7d', label: '7 días', granularity: 'day', days: 7 },
  { key: '30d', label: '30 días', granularity: 'day', days: 30 },
  { key: '90d', label: '90 días', granularity: 'day', days: 90 },
  { key: '12w', label: '12 semanas', granularity: 'week', days: 84 },
];

function shortCOP(n) {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v}`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-border rounded-sm shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold mb-1">{label}</div>
      <div>Ingresos: <span className="font-mono font-semibold">{formatCOP(p.revenue)}</span></div>
      <div className="text-muted-foreground">{p.count} {p.count === 1 ? 'transacción' : 'transacciones'}</div>
    </div>
  );
}

function RevenueChart({ data, range, onRangeChange }) {
  const active = RANGE_PRESETS.find(r => r.granularity === range.granularity && r.days === range.days) || RANGE_PRESETS[1];
  const points = data?.points || [];
  const hasAny = points.some(p => p.revenue > 0);
  const total = points.reduce((s, p) => s + (p.revenue || 0), 0);

  return (
    <div className="bg-white border border-border rounded-md p-4" data-testid="revenue-chart-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase text-muted-foreground">Tendencia de ingresos</div>
          <div className="text-lg font-heading font-bold uppercase tracking-tight">
            {formatCOP(total)} <span className="text-xs font-normal text-muted-foreground normal-case">({active.label})</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="chart-range-picker">
          {RANGE_PRESETS.map(preset => {
            const isActive = preset.key === active.key;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => onRangeChange({ granularity: preset.granularity, days: preset.days })}
                className={`px-3 py-1 rounded-sm text-xs border transition-colors ${isActive ? 'bg-accent text-white border-accent' : 'bg-white text-foreground border-border hover:bg-secondary'}`}
                data-testid={`chart-range-${preset.key}`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="h-40 w-full" data-testid="revenue-chart">
        {points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin datos</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tickFormatter={shortCOP} fontSize={11} tickLine={false} axisLine={false} width={50} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'hsl(var(--accent))', strokeDasharray: '3 3' }} />
              <Area type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#revGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {!hasAny && points.length > 0 && (
        <div className="text-xs text-muted-foreground text-center mt-2">Aún no hay ingresos pagados en este rango.</div>
      )}
    </div>
  );
}
